
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import Logo from '@/components/common/Logo';
import { useAuth } from '@/contexts/AuthContext';
import { TermsHeader } from '@/components/terms/TermsHeader';
import {
  AcceptanceSection,
  ServiceDescriptionSection,
  UserAccountsSection,
  SocialMediaIntegrationSection
} from '@/components/terms/ServiceSections';
import {
  AcceptableUseSection,
  DataPrivacySection,
  ServiceAvailabilitySection
} from '@/components/terms/PolicySections';
import {
  ServiceLimitationsSection,
  DataUsageRightsSection
} from '@/components/terms/ServiceLimitationSections';
import {
  IntellectualPropertySection,
  LimitationLiabilitySection,
  TerminationSection,
  ChangesToTermsSection,
  GoverningLawSection,
  ContactInformationSection
} from '@/components/terms/LegalSections';

const Terms = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <meta name="tiktok-developers-site-verification" content="gz0ki5g2bT4epYsqKVKhwHoMjuXwzYZA" />
      </Helmet>
      {/* Header */}
      <header className="bg-black/90 backdrop-blur-sm sticky top-0 z-50 border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/"
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-emerald-700 rounded-sm rotate-45 flex items-center justify-center">
                <span className="text-white font-bold text-sm rotate-[-45deg]">S</span>
              </div>
              <span className="text-2xl font-bold text-white tracking-tight hover:text-white/80 transition-colors">SocialScoop.ai</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-white/80 hover:text-white font-medium tracking-wide">HOME</Link>
              <Link to="/features" className="text-white/80 hover:text-white font-medium tracking-wide">FEATURES</Link>
              <Link to="/about" className="text-white/80 hover:text-white font-medium tracking-wide">ABOUT</Link>
            </nav>
            <div className="flex items-center space-x-4">
              {user ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-white text-black hover:bg-gray-100 font-medium tracking-wide"
                >
                  GO TO DASHBOARD
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/auth')}
                    className="text-white hover:bg-white/10 font-medium tracking-wide"
                  >
                    SIGN IN
                  </Button>
                  <Button 
                    onClick={handleGetStarted}
                    className="bg-white text-black hover:bg-gray-100 font-medium tracking-wide"
                  >
                    GET STARTED
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <TermsHeader />
          <div className="space-y-6">
            <AcceptanceSection />
            <ServiceDescriptionSection />
            <UserAccountsSection />
            <SocialMediaIntegrationSection />
            <ServiceLimitationsSection />
            <DataUsageRightsSection />
            <AcceptableUseSection />
            <DataPrivacySection />
            <ServiceAvailabilitySection />
            <IntellectualPropertySection />
            <LimitationLiabilitySection />
            <TerminationSection />
            <ChangesToTermsSection />
            <GoverningLawSection />
            <ContactInformationSection />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
